import React, { useState, useEffect } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Typography, Alert, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { API_URL } from '../../../utils/apiConfig';

// Per-store Employee Handbook PDFs, keyed by locations.id. Files live in
// public/static-forms/handbooks/ and build to /static-forms/handbooks/*.pdf
// (same-origin, so the iframe is CSP-safe — same pattern as the W-4 preview).
// A store with no entry here falls back to the generic acknowledgement text.
const STORE_HANDBOOKS = {
  1: { name: 'The Chipped Cup', pdf: '/static-forms/handbooks/chipped-cup.pdf' },
  2: { name: 'Mess Hall', pdf: '/static-forms/handbooks/mess-hall.pdf' },
  3: { name: 'Double Dutch', pdf: '/static-forms/handbooks/double-dutch.pdf' },
};

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL

export default function HandbookAgreementForm({ 
  employeeId, 
  formId, 
  onSubmit,
  isCompleted: initialIsCompleted 
}) {
  const [consent, setConsent] = useState(false);
  const [isCompleted, setIsCompleted] = useState(initialIsCompleted);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchingStatus, setFetchingStatus] = useState(true);
  const [employeeData, setEmployeeData] = useState(null);
  const [completedAt, setCompletedAt] = useState(null);
  const [submissionTime, setSubmissionTime] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Fetch employee and store data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        
        const empResponse = await fetch(`${API_URL}/employees/${employeeId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
  
        if (!empResponse.ok) throw new Error('Failed to fetch employee data');
        const empData = await empResponse.json();
        setEmployeeData(empData);
      } catch (err) {
        console.error('Error:', err);
        setError('Failed to load employee data');
      }
    };
  
    fetchData();
  }, [employeeId]);

  // Fetch form completion status
  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/forms/get-form-data/${formId}/${employeeId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
    
        if (!response.ok) throw new Error('Failed to fetch form data');
        const data = await response.json();
        
        if (data.isCompleted || data.documentStatus === 'Completed') {
          setConsent(true);
          setIsCompleted(true);
          setCompletedAt(data.completedAt);
        }
      } catch (err) {
        console.error('Error:', err);
        setError('Failed to load form data');
      } finally {
        setFetchingStatus(false);
      }
    };
  
    if (employeeId && formId) {
      fetchFormData();
    }
  }, [employeeId, formId]);

  const handleConsentChange = (event) => {
    setConsent(event.target.checked);
  };

  const handleSubmit = async () => {
    if (!consent) return;

    console.log('📋 [HandbookAgreementForm] Starting submission...');
    console.log('📋 [HandbookAgreementForm] Form ID:', formId);
    console.log('📋 [HandbookAgreementForm] Employee ID:', employeeId);
    
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      console.log('📋 [HandbookAgreementForm] Submitting form data to:', `${API_URL}/forms/submit`);
      
      const submitResponse = await fetch(`${API_URL}/forms/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          formId,
          employeeId,
          formData: {
            consent: true
          }
        })
      });

      console.log('📋 [HandbookAgreementForm] Submit response status:', submitResponse.status);
      if (!submitResponse.ok) {
        const errorData = await submitResponse.text();
        console.error('📋 [HandbookAgreementForm] Submit failed:', errorData);
        throw new Error('Failed to submit form');
      }

      console.log('📋 [HandbookAgreementForm] Updating form status...');
      const statusResponse = await fetch(`${API_URL}/employee_forms/update-status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          employeeId,
          formId,
          isCompleted: true,
          documentStatus: 'Completed'
        })
      });

      console.log('📋 [HandbookAgreementForm] Status update response:', statusResponse.status);
      if (!statusResponse.ok) {
        const errorData = await statusResponse.text();
        console.error('📋 [HandbookAgreementForm] Status update failed:', errorData);
        throw new Error('Failed to update form status');
      }

      console.log('✅ [HandbookAgreementForm] Form submitted successfully!');
      setIsCompleted(true);
      setCompletedAt(submissionTime);
      // Pass both formId and filePath (null for non-document forms)
      if (onSubmit) {
        console.log('📋 [HandbookAgreementForm] Calling onSubmit callback...');
        onSubmit(formId, null);
      }

    } catch (err) {
      console.error('❌ [HandbookAgreementForm] Error during submission:', err);
      setError(err.message || 'Failed to submit form');
    } finally {
      setLoading(false);
      console.log('📋 [HandbookAgreementForm] Submission process complete');
    }
  };

  const renderHandbookContent = () => {
    const locationName = employeeData?.locationName || '[Location name]';
    const employeeName = employeeData ?
      `${employeeData.firstName} ${employeeData.lastName}` :
      '[employee name]';

    const formattedDate = completedAt
      ? new Date(completedAt).toLocaleDateString()
      : '[Date of acknowledgment]';

    // If this store has a real handbook PDF, present the consent form with a
    // link to download/open it (not embedded inline — employees download and
    // review the actual document, then acknowledge).
    const handbook = employeeData ? STORE_HANDBOOKS[employeeData.locationId] : null;
    if (handbook) {
      return (
        <Box
          sx={{
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: isMobile ? '12px' : '16px',
            backgroundColor: '#f9f9f9',
            width: '100%',
            opacity: isCompleted ? 0.9 : 1,
          }}
        >
          <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 'bold', mb: 2, wordBreak: 'break-word' }}>
            {handbook.name} Employee Handbook
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please download and review the full Employee Handbook for {handbook.name}. By
            checking the box and submitting, you acknowledge that you have received and
            reviewed it and agree to abide by its policies.
          </Typography>
          <Button
            variant="contained"
            component="a"
            href={handbook.pdf}
            target="_blank"
            rel="noopener noreferrer"
            download
            sx={{ mb: 3 }}
          >
            Download the Employee Handbook (PDF)
          </Button>
          <Typography variant="body2">
            <strong>Employee Name (Printed):</strong> {employeeName}
          </Typography>
          <Typography variant="body2">
            <strong>Date:</strong> {formattedDate}
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          height: isMobile ? 'auto' : '800px',
          overflowY: isMobile ? 'visible' : 'auto',
          border: '1px solid #ddd',
          borderRadius: '4px',
          padding: isMobile ? '12px' : '16px',
          backgroundColor: '#f9f9f9',
          width: '100%',
          opacity: isCompleted ? 0.9 : 1
        }}
      >
        <Box>
          <Typography variant={isMobile ? "h6" : "h4"} sx={{ mb: 2, wordBreak: 'break-word' }}>
            Welcome to {locationName}!
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            We are thrilled to have you as part of our team. This Employee Handbook is designed to introduce you to our company's culture, policies, and benefits. Please read it carefully and feel free to reach out to the Human Resources (HR) department with any questions.
          </Typography>
          
          <Typography variant={isMobile ? "subtitle1" : "h5"} sx={{ mb: 1, fontWeight: 'bold' }}>
            1. At-Will Employment
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Your employment with {locationName} is at-will. This means that either you or the company can terminate the employment relationship at any time, with or without cause or notice, unless otherwise specified in a written agreement signed by both you and an authorized company representative.
          </Typography>
          
          <Typography variant={isMobile ? "subtitle1" : "h5"} sx={{ mb: 1, fontWeight: 'bold', wordBreak: 'break-word' }}>
            Employee Acknowledgment
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            I, {employeeName}, acknowledge that I have received and reviewed the {locationName} Employee Handbook. I understand that it is my responsibility to read and comply with the policies contained in this handbook and any revisions made to it.
          </Typography>
          
          <Typography variant={isMobile ? "subtitle1" : "h5"} sx={{ mb: 1, fontWeight: 'bold', wordBreak: 'break-word' }}>
            Electronic Acknowledgment
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            To streamline the acknowledgment process, {locationName} utilizes electronic signatures. By checking the box below and submitting this form, you agree to the terms and conditions outlined in the Employee Handbook.
          </Typography>
          
          <Typography variant="body2">
            <strong>Employee Name (Printed):</strong> {employeeName}
          </Typography>
          <Typography variant="body2">
            <strong>Date:</strong> {formattedDate}
          </Typography>
        </Box>
      </Box>
    );
  };

  if (fetchingStatus) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isCompleted) {
    return (
      <Box sx={{ 
        p: 2, 
        pb: isMobile ? 10 : 2, // Extra bottom padding on mobile for URL bar
        maxWidth: '800px', 
        margin: 'auto' 
      }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          Thank you! Your acknowledgment has been recorded.
        </Alert>
        {renderHandbookContent()}
        <FormControlLabel
          control={
            <Checkbox
              checked={true}
              disabled
              name="consent"
              color="primary"
              sx={{ alignSelf: 'flex-start', mt: 0.5 }}
            />
          }
          label="I have read and understood the Employee Handbook and agreed to abide by its policies and procedures."
          sx={{ 
            mt: 2, 
            color: 'text.secondary',
            alignItems: 'flex-start',
            '& .MuiFormControlLabel-label': {
              fontSize: isMobile ? '0.875rem' : '1rem',
              lineHeight: 1.5,
              paddingTop: '2px'
            }
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 2, 
      pb: isMobile ? 10 : 2, // Extra bottom padding on mobile for URL bar
      maxWidth: '800px', 
      margin: 'auto' 
    }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {renderHandbookContent()}

      <FormControlLabel
        control={
          <Checkbox
            checked={consent}
            onChange={handleConsentChange}
            name="consent"
            color="primary"
            disabled={loading}
            sx={{ alignSelf: 'flex-start', mt: 0.5 }}
          />
        }
        label="I acknowledge that I have read and understood the Employee Handbook and agree to abide by its policies and procedures."
        sx={{ 
          mt: 2,
          alignItems: 'flex-start',
          '& .MuiFormControlLabel-label': {
            fontSize: isMobile ? '0.875rem' : '1rem',
            lineHeight: 1.5,
            paddingTop: '2px'
          }
        }}
      />

      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!consent || loading}
        >
          Submit
        </Button>
        {loading && <CircularProgress size={24} />}
      </Box>
    </Box>
  );
}